import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Compact dock HUD when the stats bar scrolls out of view during a live scan.
 * Portaled to document.body so it never steals a home-page-shell grid row
 * (that clipped the pinned footer on mobile / landscape).
 *
 * @param {{
 *   active?: boolean,
 *   resultsCount?: number,
 *   observeTargetRef?: { current: Element | null },
 *   metricsInView?: boolean | null,
 *   onCancel?: () => void,
 * }} props
 */
export default function FloatingScanHud({
  active = false,
  resultsCount = 0,
  observeTargetRef,
  /** When boolean, parent scroll/sticky logic wins over IntersectionObserver. */
  metricsInView = null,
  onCancel,
}) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [statusOutOfView, setStatusOutOfView] = useState(false);
  const startRef = useRef(null);
  const rafRef = useRef(null);
  const useParentVisibility = typeof metricsInView === 'boolean';

  useEffect(() => {
    if (!active) {
      setStatusOutOfView(false);
      startRef.current = null;
      setElapsedMs(0);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    startRef.current = performance.now();
    const tick = (now) => {
      if (startRef.current != null) setElapsedMs(now - startRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  useEffect(() => {
    if (!active || useParentVisibility) {
      if (!active) setStatusOutOfView(false);
      return undefined;
    }
    const el = observeTargetRef?.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setStatusOutOfView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        setStatusOutOfView(!entry?.isIntersecting);
      },
      { root: null, threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [active, observeTargetRef, useParentVisibility]);

  const timer = useMemo(() => {
    const totalSec = Math.floor(elapsedMs / 1000);
    const mm = Math.floor(totalSec / 60);
    const ss = totalSec % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }, [elapsedMs]);

  const outOfView = useParentVisibility ? !metricsInView : statusOutOfView;
  if (!active || !outOfView) return null;

  const hud = (
    <div
      className="pointer-events-none fixed z-[60] bottom-[max(1.5rem,calc(env(safe-area-inset-bottom)+1.5rem))] right-6 sm:bottom-6 sm:right-6"
      aria-hidden={false}
      data-testid="floating-scan-hud-anchor"
    >
      <div
        className="pointer-events-auto flex max-w-[min(100%,20rem)] items-center gap-2 rounded-full border border-cyan-500/35 bg-slate-950/92 px-2.5 py-1.5 text-[11px] text-cyan-100 shadow-[0_8px_32px_rgba(0,0,0,0.45)] backdrop-blur-md sm:gap-2.5 sm:px-3 sm:text-xs"
        role="status"
        aria-live="polite"
        data-testid="floating-scan-hud"
      >
        <span
          className="home-scan-hud-pulse h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400"
          aria-hidden
        />
        <span className="inline-flex min-w-0 items-center gap-1.5 font-mono tabular-nums tracking-tight">
          <span className="text-cyan-300/90">{timer}</span>
          <span className="text-cyan-700" aria-hidden>
            ·
          </span>
          <span className="truncate font-semibold uppercase tracking-[0.12em] text-cyan-200/95">
            Scanning
          </span>
          <span className="text-cyan-700" aria-hidden>
            ·
          </span>
          <span data-testid="floating-scan-results" className="text-slate-300">
            {Number(resultsCount) || 0}
          </span>
        </span>
        <button
          type="button"
          onClick={() => onCancel?.()}
          className="inline-flex h-7 shrink-0 items-center justify-center rounded-full border border-rose-400/35 bg-rose-500/10 px-2.5 text-[10px] font-bold uppercase tracking-wider text-rose-100 transition hover:bg-rose-500/20"
          aria-label="Cancel scan"
          data-testid="floating-scan-cancel"
        >
          ✕
        </button>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return hud;
  return createPortal(hud, document.body);
}
